// App component with SEO integration and code splitting
import { useState, useEffect, lazy, Suspense } from 'react';
import Layout from './components/Layout';
import GeoLocationCheck from './components/GeoLocationCheck';
import { LocationProvider } from './contexts/LocationContext';
import { useReminderChecker } from './utils/reminderChecker';
import LoadingSpinner from './components/LoadingSpinner';
import { applySEOForPath } from './utils/seo';
import { isSupabaseConfigured } from './lib/supabase';

// Lazy load all page components for better code splitting
const HomePage = lazy(() => import('./pages/HomePage'));
const CreateGroupPage = lazy(() => import('./pages/CreateGroupPage'));
const GiftFinderPage = lazy(() => import('./pages/GiftFinderPage'));
const GroupDashboardPage = lazy(() => import('./pages/GroupDashboardPage'));
const ParticipantJoinPage = lazy(() => import('./pages/ParticipantJoinPage'));
const ParticipantOnboardingPage = lazy(() => import('./pages/ParticipantOnboardingPage'));
const GiftSelectionPortalPage = lazy(() => import('./pages/GiftSelectionPortalPage'));
const NameDrawingResultPage = lazy(() => import('./pages/NameDrawingResultPage'));
const ChatPage = lazy(() => import('./pages/ChatPage'));
const WishlistPage = lazy(() => import('./pages/WishlistPage'));
const RecipientWishlistPage = lazy(() => import('./pages/RecipientWishlistPage'));
const AdminDashboardPage = lazy(() => import('./pages/AdminDashboardPage'));
const LinkRecoveryPage = lazy(() => import('./pages/LinkRecoveryPage'));
const HowItWorksPage = lazy(() => import('./pages/HowItWorksPage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TechPage = lazy(() => import('./pages/TechPage'));
const FeedbackPage = lazy(() => import('./pages/FeedbackPage'));
const SupportPage = lazy(() => import('./pages/SupportPage'));
const BudgetCalculatorPage = lazy(() => import('./pages/BudgetCalculatorPage'));
const GiftIdeasGeneratorPage = lazy(() => import('./pages/GiftIdeasGeneratorPage'));
const ImageMigrationPage = lazy(() => import('./pages/ImageMigrationPage'));
const RefundPolicyPage = lazy(() => import('./pages/RefundPolicyPage'));
const AutoAcceptPage = lazy(() => import('./pages/AutoAcceptPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// Event pages
const ChristmasPage = lazy(() => import('./pages/events/ChristmasPage'));
const OfficePartyPage = lazy(() => import('./pages/events/OfficePartyPage'));
const BirthdayPage = lazy(() => import('./pages/events/BirthdayPage'));
const WeddingPage = lazy(() => import('./pages/events/WeddingPage'));

// Blog pages
const UltimateGuideSecretSantaLargeGroupsPage = lazy(() => import('./pages/blog/UltimateGuideSecretSantaLargeGroupsPage'));
const CreativeGiftExchangeIdeasPage = lazy(() => import('./pages/blog/CreativeGiftExchangeIdeasPage'));
const PerfectBudgetGiftExchangePage = lazy(() => import('./pages/blog/PerfectBudgetGiftExchangePage'));
const VirtualGiftExchangesPage = lazy(() => import('./pages/blog/VirtualGiftExchangesPage'));
const SwapgiftsAlgorithmSciencePage = lazy(() => import('./pages/blog/SwapgiftsAlgorithmSciencePage'));
const WishlistBestPracticesPage = lazy(() => import('./pages/blog/WishlistBestPracticesPage'));
const OfficeGiftExchangeEtiquettePage = lazy(() => import('./pages/blog/OfficeGiftExchangeEtiquettePage'));
const InternationalGiftExchangesPage = lazy(() => import('./pages/blog/InternationalGiftExchangesPage'));
const LastMinuteGiftIdeasPage = lazy(() => import('./pages/blog/LastMinuteGiftIdeasPage'));
const ChristmasGiftExchangeTraditionsPage = lazy(() => import('./pages/blog/ChristmasGiftExchangeTraditionsPage'));
const HanukkahGiftGivingGuidePage = lazy(() => import('./pages/blog/HanukkahGiftGivingGuidePage'));
const DiwaliGiftExchangeIdeasPage = lazy(() => import('./pages/blog/DiwaliGiftExchangeIdeasPage'));
const LunarNewYearGiftTraditionsPage = lazy(() => import('./pages/blog/LunarNewYearGiftTraditionsPage'));
const ValentinesDayGiftExchangesPage = lazy(() => import('./pages/blog/ValentinesDayGiftExchangesPage'));
const MothersFathersDayGiftIdeasPage = lazy(() => import('./pages/blog/MothersFathersDayGiftIdeasPage'));
const EidGiftGivingTraditionsPage = lazy(() => import('./pages/blog/EidGiftGivingTraditionsPage'));
const ThanksgivingGratitudeExchangesPage = lazy(() => import('./pages/blog/ThanksgivingGratitudeExchangesPage'));
const BirthdayGroupGiftCoordinationPage = lazy(() => import('./pages/blog/BirthdayGroupGiftCoordinationPage'));
const WeddingShowerGiftExchangesPage = lazy(() => import('./pages/blog/WeddingShowerGiftExchangesPage'));

function App() {
  if (!isSupabaseConfigured) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '2rem',
        background: 'linear-gradient(to bottom right, #fee2e2, #fef3c7)',
        fontFamily: 'system-ui, -apple-system, sans-serif'
      }}>
        <div style={{
          maxWidth: '32rem',
          background: 'white',
          padding: '2rem',
          borderRadius: '0.5rem',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
        }}>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem', color: '#991b1b' }}>
            Configuration Error
          </h1>
          <p style={{ marginBottom: '1rem', color: '#4b5563' }}>
            The application is missing required environment variables:
          </p>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem', color: '#6b7280' }}>
            <li>VITE_SUPABASE_URL</li>
            <li>VITE_SUPABASE_ANON_KEY</li>
          </ul>
          <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
            These must be set in your hosting environment's build configuration.
          </p>
        </div>
      </div>
    );
  }
  const [currentPath, setCurrentPath] = useState(window.location.pathname);

  // Check and process scheduled reminders every minute
  useReminderChecker();

  // Sync with browser navigation (back/forward)
  useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(window.location.pathname);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Apply SEO metadata on route change
  useEffect(() => {
    applySEOForPath(currentPath);
  }, [currentPath]);

  const renderPage = () => {
    const path = currentPath;
    if (path === '/') return <HomePage />;
    if (path === '/create') return <CreateGroupPage />;
    if (path === '/gift-finder') return <GiftFinderPage />;
    if (path === '/budget-calculator') return <BudgetCalculatorPage />;
    if (path === '/gift-ideas') return <GiftIdeasGeneratorPage />;
    if (path === '/how-it-works') return <HowItWorksPage />;
    if (path === '/about') return <AboutPage />;
    if (path === '/faq') return <FAQPage />;
    if (path === '/blog/ultimate-guide-secret-santa-large-groups') return <UltimateGuideSecretSantaLargeGroupsPage />;
    if (path === '/blog/creative-gift-exchange-ideas') return <CreativeGiftExchangeIdeasPage />;
    if (path === '/blog/perfect-budget-gift-exchange') return <PerfectBudgetGiftExchangePage />;
    if (path === '/blog/virtual-gift-exchanges') return <VirtualGiftExchangesPage />;
    if (path === '/blog/swapgifts-algorithm-science') return <SwapgiftsAlgorithmSciencePage />;
    if (path === '/blog/wishlist-best-practices') return <WishlistBestPracticesPage />;
    if (path === '/blog/office-gift-exchange-etiquette') return <OfficeGiftExchangeEtiquettePage />;
    if (path === '/blog/international-gift-exchanges') return <InternationalGiftExchangesPage />;
    if (path === '/blog/last-minute-gift-ideas') return <LastMinuteGiftIdeasPage />;
    if (path === '/blog/christmas-gift-exchange-traditions') return <ChristmasGiftExchangeTraditionsPage />;
    if (path === '/blog/hanukkah-gift-giving-guide') return <HanukkahGiftGivingGuidePage />;
    if (path === '/blog/diwali-gift-exchange-ideas') return <DiwaliGiftExchangeIdeasPage />;
    if (path === '/blog/lunar-new-year-gift-traditions') return <LunarNewYearGiftTraditionsPage />;
    if (path === '/blog/valentines-day-gift-exchanges') return <ValentinesDayGiftExchangesPage />;
    if (path === '/blog/mothers-fathers-day-gift-ideas') return <MothersFathersDayGiftIdeasPage />;
    if (path === '/blog/eid-gift-giving-traditions') return <EidGiftGivingTraditionsPage />;
    if (path === '/blog/thanksgiving-gratitude-exchanges') return <ThanksgivingGratitudeExchangesPage />;
    if (path === '/blog/birthday-group-gift-coordination') return <BirthdayGroupGiftCoordinationPage />;
    if (path === '/blog/wedding-shower-gift-exchanges') return <WeddingShowerGiftExchangesPage />;
    if (path.startsWith('/blog/')) return <BlogPostPage />;
    if (path === '/blog') return <BlogPage />;
    if (path === '/terms') return <TermsPage />;
    if (path === '/privacy') return <PrivacyPage />;
    if (path === '/refund-policy') return <RefundPolicyPage />;
    if (path === '/autoaccept') return <AutoAcceptPage />;
    if (path === '/tech') return <TechPage />;
    if (path === '/feedback') return <FeedbackPage />;
    if (path === '/support' || path === '/contact') return <SupportPage />;
    if (path === '/recover') return <LinkRecoveryPage />;
    if (path === '/admin') return <AdminDashboardPage />;
    if (path === '/admin/images') return <ImageMigrationPage />;
    if (path === '/events/christmas' || path === '/christmas-secret-santa') return <ChristmasPage />;
    if (path === '/events/hanukkah') return <ChristmasPage />;
    if (path === '/events/office-party') return <OfficePartyPage />;
    if (path === '/events/birthday') return <BirthdayPage />;
    if (path === '/events/wedding') return <WeddingPage />;
    if (path.startsWith('/group/')) return <GroupDashboardPage />;
    if (path.startsWith('/join/')) return <ParticipantJoinPage />;
    if (path.startsWith('/participant/') && path.includes('/gifts')) return <GiftSelectionPortalPage />;
    if (path.startsWith('/participant/') && path.includes('/chat')) return <ChatPage />;
    if (path.startsWith('/participant/') && path.includes('/drawing')) return <NameDrawingResultPage />;
    if (path.startsWith('/participant/') && !path.includes('/gifts') && !path.includes('/chat') && !path.includes('/drawing'))
      return <ParticipantOnboardingPage />;
    if (path.startsWith('/wishlist/')) return <RecipientWishlistPage />;

    if (path === '/' || path === '') return <HomePage />;
    return <NotFoundPage />;
  };

  const handleLinkClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    const anchor = target.closest('a');
    if (anchor && anchor.href.startsWith(window.location.origin)) {
      const url = new URL(anchor.href);
      const shouldPreventDefault =
        !url.pathname.startsWith('/join/') &&
        !url.pathname.startsWith('/wishlist/') &&
        !url.pathname.startsWith('/participant/');

      if (shouldPreventDefault) {
        e.preventDefault();
        window.history.pushState({}, '', url.pathname + url.search);
        setCurrentPath(url.pathname);
        window.scrollTo(0, 0);
      }
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleLinkClick);
    return () => document.removeEventListener('click', handleLinkClick);
  }, []);

  return (
    <LocationProvider>
      <GeoLocationCheck />
      <Layout>
        <Suspense fallback={<LoadingSpinner />}>
          {renderPage()}
        </Suspense>
      </Layout>
    </LocationProvider>
  );
}

export default App;
